import { PhoneLoginKind, type UserDataLoginRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BrandType } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { ErrorCodes } from 'noddi-ui-web';
import { commonTexts, errorCodesEnglish, getPhoneLoginOutcome, LanguageCodeType } from 'noddi-util';
import { useRef, useState } from 'react';
import type { UserSessionData } from '../../contexts/AuthContext';
import { useAuthContext } from '../../contexts/useAuthContext';
import { commonTrackingEvents, tracking } from '../../tracking';
import { UserRoleType } from '../../types/roles';
import { AccountSelectionStep } from './AccountSelectionStep';
import { finalizeLoginSession } from './loginSession';
import { PhoneNumberForm } from './PhoneNumberForm';
import type { SMSLoginStepLayout } from './smsLoginStep';
import { phoneNumberDto } from './util';
import { VerificationCodeForm } from './VerificationCodeForm';

type SignupRequiredContext = {
  phoneNumber: string;
  signupChallengeToken: string;
  onRestartVerification: () => void;
  onLoginSuccess?: (userData: UserSessionData) => void;
};

type Props = {
  userRegistrationForm: (signup: SignupRequiredContext) => React.ReactElement;
  className?: string;
  hidePhoneNumberText?: boolean;
  /** The phone field does not take focus on mount, e.g. beside the page's own first field. */
  disablePhoneAutoFocus?: boolean;
  /** Field label above the phone input. */
  phoneNumberLabel?: string;
  /** Send-code button sits beside the field, under it on phones — for a compact card. */
  inlineSubmit?: boolean;
  /** Rendered under the send-code button, on the phone step only. */
  phoneStepFooter?: React.ReactNode;
  /**
   * Lays out every step as a page: the step's heading and where its submit goes, e.g. a fixed footer.
   * Without it, each step renders its own copy and an inline submit.
   */
  renderStep?: (step: SMSLoginStepLayout) => React.ReactNode;
  trackUserIdentity?: (userData: UserDataLoginRecord) => void;
  language?: LanguageCodeType;
  brandName: BrandType;
  /**
   * The workspace the surrounding flow belongs to, e.g. the user group a booking
   * proposal was made for. A phone shared by several accounts resolves to the one
   * member of that group, so the picker only appears on a real choice.
   */
  userGroupId?: number;
  onLoginSuccess?: (userData: UserSessionData) => void;
  requiredLoginLevels?: UserRoleType[];
  setErrorMessage?: React.Dispatch<React.SetStateAction<string>>;
  translations?: {
    phoneNumber?: string;
    enterCode: string;
    confirm: string;
    logIn?: string;
    changeNumber?: string;
    sendNewCode?: string;
    enterNumberToReceiveVerificationCode: string;
    resend: string;
    sendCode: string;
    didntReceiveSms: string;
    sentTo: string;
    accessDeniedLoginMessage: string;
    chooseAccount?: string;
    chooseAccountDescription?: string;
    back?: string;
    personalAccountPrivate?: string;
    personalAccountBusiness?: string;
  };
  errorCodes?: ErrorCodes;
};

// Since the worker app and the customer app are using the same codebase, we need to
// allow for different user registration forms. This is why provide a callback function
export const SMSLogin = ({
  userRegistrationForm,
  className,
  hidePhoneNumberText,
  disablePhoneAutoFocus,
  phoneNumberLabel,
  inlineSubmit,
  phoneStepFooter,
  renderStep,
  trackUserIdentity,
  userGroupId,
  onLoginSuccess,
  requiredLoginLevels,
  setErrorMessage,
  translations,
  language,
  brandName,
  errorCodes
}: Props) => {
  const { loginUser } = useAuthContext();
  const [mobileNumber, setMobileNumber] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const phoneNumber = phoneNumberDto({ mobileNumber });
  // Verification attempts this session — repeated submits signal a delivery or UX problem.
  const verifyAttempt = useRef(0);

  const texts = translations ?? commonTexts.smsLoginTranslations;
  const errorCodeTexts = errorCodes ?? errorCodesEnglish;

  const finalizeSession = (session: UserSessionData) => {
    finalizeLoginSession({
      session,
      loginUser,
      requiredLoginLevels,
      accessDeniedMessage: texts.accessDeniedLoginMessage,
      onAccessDenied: (message) => setErrorMessage?.(message),
      trackUserIdentity,
      onLoginSuccess
    });
  };

  const {
    mutate,
    isPending,
    isSuccess: verificationSuccess,
    data,
    error,
    reset
  } = noddiAsync.usePost({
    type: URLKeys.postVerifyPhoneNumber,
    queryConfig: {
      onSuccess(res) {
        const outcome = getPhoneLoginOutcome(res.data);
        if (outcome?.kind === PhoneLoginKind.LOGGED_IN) {
          finalizeSession(outcome);
        }
      },
      onError(error) {
        tracking.track(commonTrackingEvents.loginFailed, {
          reason: error instanceof Error ? error.message : undefined
        });
      }
    }
  });

  const outcome = verificationSuccess && data?.data ? getPhoneLoginOutcome(data.data) : null;

  if (outcome?.kind === PhoneLoginKind.ACCOUNT_SELECTION) {
    const accountSelection = (
      <AccountSelectionStep
        accountSelectionToken={outcome.accountSelectionToken}
        accounts={outcome.accounts}
        onSelected={finalizeSession}
        onBack={() => {
          reset();
          setErrorMessage?.('');
        }}
        translations={texts}
        errorCodes={errorCodeTexts}
      />
    );
    return renderStep
      ? renderStep({ kind: 'accountSelection', content: accountSelection, phoneNumber })
      : accountSelection;
  }

  if (outcome?.kind === PhoneLoginKind.SIGNUP_REQUIRED) {
    const signup = userRegistrationForm({
      phoneNumber,
      signupChallengeToken: outcome.signupChallengeToken,
      onLoginSuccess,
      onRestartVerification: () => {
        reset();
        setCodeSent(false);
        setErrorMessage?.('');
      }
    });
    return renderStep ? renderStep({ kind: 'signup', content: signup, phoneNumber }) : signup;
  }

  const changeNumber = () => {
    reset();
    setCodeSent(false);
    setErrorMessage?.('');
  };

  const step = codeSent ? (
    <VerificationCodeForm
      translations={texts}
      errorCodes={errorCodeTexts}
      error={error}
      isLoading={isPending}
      brandName={brandName}
      phoneNumber={phoneNumber}
      verificationSuccess={verificationSuccess}
      onSubmitCode={(code) => {
        verifyAttempt.current += 1;
        tracking.track(commonTrackingEvents.loginSmsCodeSubmitted, { attempt: verifyAttempt.current });
        mutate({ code, phoneNumber, userGroupId });
      }}
      renderStep={renderStep && ((parts) => renderStep({ kind: 'code', phoneNumber, ...parts }))}
      onChangeNumber={changeNumber}
    />
  ) : (
    <PhoneNumberForm
      mobileNumber={mobileNumber}
      setMobileNumber={setMobileNumber}
      onCodeSent={(sentNumber) => {
        setMobileNumber(sentNumber);
        setCodeSent(true);
      }}
      brandName={brandName}
      hideText={hidePhoneNumberText}
      disableAutoFocus={disablePhoneAutoFocus}
      phoneNumberLabel={phoneNumberLabel}
      inlineSubmit={inlineSubmit}
      footer={phoneStepFooter}
      translations={texts}
      language={language}
      errorCodes={errorCodeTexts}
      renderStep={renderStep && ((parts) => renderStep({ kind: 'phone', phoneNumber, ...parts }))}
    />
  );

  return renderStep ? step : <div className={cn('flex flex-col gap-10', className)}>{step}</div>;
};
